import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, UserCheck, X, Calendar } from "lucide-react";
import { waeAPI, fmtDT } from "./waeApi";
import { EmptyState, Modal, Fld, Footer, cls } from "./uiKit";
import { DatePicker } from "@/components/ui/date-picker";

export default function WAEDelegations() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = () => waeAPI.delegations().then(setRows);
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this delegation?")) return;
    try { await waeAPI.revokeDelegation(id); toast.success("Revoked"); load(); }
    catch { toast.error("Failed"); }
  };

  const now = new Date();
  const isActive = (d) => d.active && new Date(d.starts_at) <= now && new Date(d.ends_at) >= now;

  return (
    <div className="space-y-4" data-testid="wae-delegations">
      <div className="flex items-center gap-3">
        <div className="text-[13px] text-slate-500">
          Delegate approvals during leave, travel, or acting-manager coverage. Delegates automatically inherit your inbox items during the window.
        </div>
        <button onClick={()=>setCreating(true)} data-testid="wae-deleg-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-95">
          <Plus className="w-4 h-4"/> New Delegation
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {rows.length === 0
          ? <EmptyState icon={UserCheck} title="No delegations set"
              msg="Create a temporary delegation so someone else can act on your inbox items."/>
          : (
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Delegator</th>
                  <th className="text-left px-4 py-3">Delegate To</th>
                  <th className="text-left px-4 py-3">Types</th>
                  <th className="text-left px-4 py-3">Window</th>
                  <th className="text-left px-4 py-3">Reason</th>
                  <th className="text-center px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(d => (
                  <tr key={d.id} data-testid={`wae-deleg-row-${d.id}`} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 mono text-[12px]">{d.delegator_email}</td>
                    <td className="px-4 py-3 mono text-[12px]">{d.delegate_to}</td>
                    <td className="px-4 py-3">
                      {(d.approval_type_codes || ["*"]).map(t =>
                        <span key={t} className="mono text-[11px] mr-1 px-1.5 py-0.5 rounded bg-slate-100">{t}</span>)}
                    </td>
                    <td className="px-4 py-3 text-[11.5px] text-slate-500 mono">
                      {fmtDT(d.starts_at)}<br/>→ {fmtDT(d.ends_at)}
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-500 truncate max-w-xs italic">{d.reason || "—"}</td>
                    <td className="px-4 py-3 text-center">
                      {isActive(d)
                        ? <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-emerald-100 text-emerald-700">Active</span>
                        : d.active
                          ? <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600">Scheduled</span>
                          : <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-400">Revoked</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {d.active && (
                        <button onClick={()=>revoke(d.id)} data-testid={`wae-deleg-revoke-${d.id}`}
                                className="h-8 px-3 rounded-lg text-rose-600 hover:bg-rose-50 text-[12px] font-medium">
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {creating && <CreateDelegation onClose={()=>setCreating(false)} onDone={()=>{ setCreating(false); load(); }}/>}
    </div>
  );
}

function CreateDelegation({ onClose, onDone }) {
  const [directory, setDirectory] = useState([]);
  const [types, setTypes] = useState([]);
  const [f, setF] = useState({
    delegate_to: "",
    reason: "",
    starts_at: new Date().toISOString().slice(0, 16),
    ends_at:   new Date(Date.now() + 7*86400*1000).toISOString().slice(0, 16),
    approval_type_codes: ["*"],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    waeAPI.directory("").then(setDirectory);
    waeAPI.approvalTypes().then(setTypes);
  }, []);

  const toggleType = (code) => {
    if (code === "*") { setF({...f, approval_type_codes: ["*"]}); return; }
    const cur = f.approval_type_codes.filter(x => x !== "*");
    const next = cur.includes(code) ? cur.filter(x => x !== code) : [...cur, code];
    setF({...f, approval_type_codes: next.length ? next : ["*"]});
  };

  const submit = async () => {
    if (!f.delegate_to) { toast.error("Pick a delegate"); return; }
    if (new Date(f.ends_at) <= new Date(f.starts_at)) { toast.error("End must be after start"); return; }
    setSaving(true);
    try {
      await waeAPI.createDelegation({
        delegate_to: f.delegate_to,
        reason: f.reason.trim() || null,
        starts_at: new Date(f.starts_at).toISOString(),
        ends_at: new Date(f.ends_at).toISOString(),
        approval_type_codes: f.approval_type_codes,
      });
      toast.success("Delegation created");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="New Delegation" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Delegate To" full>
          <select value={f.delegate_to} onChange={e=>setF({...f, delegate_to: e.target.value})}
                  data-testid="wae-deleg-target" className={cls}>
            <option value="">Select employee…</option>
            {directory.map(u => <option key={u.user_id} value={u.user_id}>{u.name} — {u.email}</option>)}
          </select>
        </Fld>
        <Fld label="Starts">
          <DatePicker value={(f.starts_at || '').slice(0,10)} onChange={(_v) => setF({...f, starts_at: _v})} data-testid="wae-deleg-start"/>
        </Fld>
        <Fld label="Ends">
          <DatePicker value={(f.ends_at || '').slice(0,10)} onChange={(_v) => setF({...f, ends_at: _v})} data-testid="wae-deleg-end"/>
        </Fld>
        <Fld label="Approval Types" full>
          <div className="flex flex-wrap gap-1">
            <button type="button" onClick={()=>toggleType("*")} data-testid="wae-deleg-type-all"
                    className={`h-8 px-3 rounded-lg text-[12px] font-medium border ${
                      f.approval_type_codes.includes("*") ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white text-slate-600 border-slate-200"}`}>
              All Types
            </button>
            {types.filter(t=>t.is_active).map(t => (
              <button key={t.code} type="button" onClick={()=>toggleType(t.code)}
                      data-testid={`wae-deleg-type-${t.code}`}
                      className={`h-8 px-3 rounded-lg text-[12px] font-medium border ${
                        f.approval_type_codes.includes(t.code) ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white text-slate-600 border-slate-200"}`}>
                {t.name}
              </button>
            ))}
          </div>
        </Fld>
        <Fld label="Reason (optional)" full>
          <input value={f.reason} onChange={e=>setF({...f, reason: e.target.value})}
                 data-testid="wae-deleg-reason" placeholder="e.g. Vacation Aug 12-19" className={cls}/>
        </Fld>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} submitLabel="Create Delegation" testid="wae-deleg-submit"/>
    </Modal>
  );
}
